/**
 * Catálogo central de códigos de erro.
 *
 * Cada código tem mensagem em português definida em `error-messages.ts`. Nenhum caso de
 * uso escreve texto de erro solto: o código é a chave estável usada na tela, no teste e
 * no log de diagnóstico.
 */
export const ERROR_CODE = {
  // Sessão e autorização (RN-130 a RN-136)
  SESSION_REQUIRED: 'ACCESS.SESSION_REQUIRED',
  PERMISSION_DENIED: 'ACCESS.PERMISSION_DENIED',
  PROJECT_ACCESS_DENIED: 'ACCESS.PROJECT_ACCESS_DENIED',
  USER_NOT_FOUND: 'ACCESS.USER_NOT_FOUND',
  USER_INACTIVE: 'ACCESS.USER_INACTIVE',
  USER_NAME_TAKEN: 'ACCESS.USER_NAME_TAKEN',
  USER_EMAIL_TAKEN: 'ACCESS.USER_EMAIL_TAKEN',
  ROLE_NOT_FOUND: 'ACCESS.ROLE_NOT_FOUND',
  ASSIGNMENT_EXISTS: 'ACCESS.ASSIGNMENT_EXISTS',
  LAST_ADMIN: 'ACCESS.LAST_ADMIN',
  LOGIN_LINK_INVALID: 'ACCESS.LOGIN_LINK_INVALID',
  INVALID_CREDENTIALS: 'ACCESS.INVALID_CREDENTIALS',
  SESSION_EXPIRED: 'ACCESS.SESSION_EXPIRED',
  EMAIL_REQUIRED: 'ACCESS.EMAIL_REQUIRED',
  // Chave de API (07/10/2026): a credencial do agente, só leitura
  API_KEY_INVALID: 'ACCESS.API_KEY_INVALID',
  API_KEY_READ_ONLY: 'ACCESS.API_KEY_READ_ONLY',
  API_KEY_NOT_FOUND: 'ACCESS.API_KEY_NOT_FOUND',

  // Entrada
  INVALID_INPUT: 'INPUT.INVALID',

  // Projeto
  PROJECT_NOT_FOUND: 'PROJECT.NOT_FOUND',
  PROJECT_CODE_TAKEN: 'PROJECT.CODE_TAKEN',
  // Versões de orçamento da obra (07/10/2026)
  PROJECT_IS_VERSION: 'PROJECT.IS_VERSION',
  PROJECT_NOT_A_VERSION: 'PROJECT.NOT_A_VERSION',
  PROJECT_IS_APPROVED: 'PROJECT.IS_APPROVED',
  PROJECT_HAS_EXECUTION: 'PROJECT.HAS_EXECUTION',
  // A operação muda de obra na aprovação (Fase 36 — RN-467, RN-468)
  PROJECT_VERSION_TRANSFER_CONFLICTS: 'PROJECT.VERSION_TRANSFER_CONFLICTS',
  // O serviço vai da versão para a obra aprovada (Fase 38 — RN-487 a RN-495)
  PROJECT_SERVICE_TRANSFER_NOT_A_VERSION: 'PROJECT.SERVICE_TRANSFER_NOT_A_VERSION',
  PROJECT_SERVICE_TRANSFER_UNKNOWN_SERVICE: 'PROJECT.SERVICE_TRANSFER_UNKNOWN_SERVICE',
  PROJECT_SERVICE_TRANSFER_APPROVED: 'PROJECT.SERVICE_TRANSFER_APPROVED',
  PROJECT_SERVICE_TRANSFER_GAPS: 'PROJECT.SERVICE_TRANSFER_GAPS',
  // O orçamento aprovado por serviço (Fase 33 — RN-435, RN-436, RN-440)
  PROJECT_BUDGET_SERVICE_APPROVED: 'PROJECT.BUDGET_SERVICE_APPROVED',
  PROJECT_BUDGET_LINE_WITHOUT_PRICE: 'PROJECT.BUDGET_LINE_WITHOUT_PRICE',
  PROJECT_BUDGET_IN_VERSION: 'PROJECT.BUDGET_IN_VERSION',
  PROJECT_BUDGET_SERVICES_MISSING: 'PROJECT.BUDGET_SERVICES_MISSING',
  // A quantidade travada do serviço aprovado (Fase 33 — RN-438)
  PROJECT_BUDGET_SERVICE_LOCKED: 'PROJECT.BUDGET_SERVICE_LOCKED',

  // Armazenamento (RN-105, ADR-101)
  QUOTA_EXCEEDED: 'STORAGE.QUOTA_EXCEEDED',
  STORAGE_UNAVAILABLE: 'STORAGE.UNAVAILABLE',
  FILE_NOT_FOUND: 'STORAGE.FILE_NOT_FOUND',

  // Transporte: o corpo passou do que alguém no caminho aceita (`413`). Mora aqui, e não
  // só no cliente HTTP, porque quem envia arquivo decide o que fazer com a recusa — a
  // prancha grande demais sobe como página renderizada (RN-248).
  REQUEST_TOO_LARGE: 'API.CORPO_GRANDE_DEMAIS',
  // Transporte: não houve resposta. Mora aqui pelo mesmo motivo: a abertura do sistema
  // distingue "sem sinal" de "sessão recusada", e só a segunda esquece o token (RN-598).
  NO_CONNECTION: 'API.SEM_CONEXAO',
  // Houve resposta, e não era a da API: portal do Wi-Fi, proxy, a página de erro do provedor.
  // Para a fila do aparelho é o mesmo que não ter sinal (Fase 43).
  UNREADABLE_RESPONSE: 'API.RESPOSTA_ILEGIVEL',
  // O id que o aparelho mandou já serviu a outra gravação, de outra pessoa ou em outro caminho
  // (Fase 43, RN-606). Não é para acontecer: o id nasce no aparelho, um por passo.
  DEVICE_OPERATION_MISMATCH: 'OFFLINE.OPERACAO_TROCADA',
  // O mesmo passo chegou duas vezes ao mesmo tempo — duas abas reenviando a fila.
  DEVICE_OPERATION_BUSY: 'OFFLINE.OPERACAO_EM_ANDAMENTO',

  // Inesperado
  UNEXPECTED: 'SYSTEM.UNEXPECTED',
} as const;

export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];

/**
 * Mensagens ao usuário, em português e sem jargão técnico.
 * Códigos não listados caem na mensagem genérica — nunca em stack trace.
 */
export const ERROR_MESSAGE: Readonly<Record<string, string>> = {
  [ERROR_CODE.SESSION_REQUIRED]: 'Selecione um perfil antes de continuar.',
  [ERROR_CODE.PERMISSION_DENIED]: 'Seu perfil não tem permissão para esta ação.',
  [ERROR_CODE.PROJECT_ACCESS_DENIED]: 'Obra não encontrada.',
  [ERROR_CODE.USER_NOT_FOUND]: 'Perfil não encontrado.',
  [ERROR_CODE.USER_INACTIVE]: 'Este perfil está inativo e não pode operar o sistema.',
  [ERROR_CODE.USER_NAME_TAKEN]: 'Já existe um perfil com este nome.',
  [ERROR_CODE.USER_EMAIL_TAKEN]:
    'Já existe um perfil com este e-mail. Um endereço identifica uma pessoa — é por ele que ela entra.',
  [ERROR_CODE.ROLE_NOT_FOUND]: 'Papel não encontrado.',
  [ERROR_CODE.ASSIGNMENT_EXISTS]: 'Este papel já está atribuído a este perfil.',
  [ERROR_CODE.LAST_ADMIN]:
    'Não é possível remover o último administrador — o sistema ficaria sem quem administra perfis.',
  [ERROR_CODE.LOGIN_LINK_INVALID]:
    'Este link não vale mais — ou já foi usado, ou passou do prazo. Peça outro.',
  [ERROR_CODE.SESSION_EXPIRED]: 'Sua sessão expirou. Entre novamente.',
  // Uma mensagem para as quatro recusas — e-mail inexistente, senha errada, perfil sem
  // senha e perfil inativo. Separá-las diria a quem tenta entrar qual metade acertou.
  [ERROR_CODE.INVALID_CREDENTIALS]: 'E-mail ou senha incorretos.',
  [ERROR_CODE.EMAIL_REQUIRED]:
    'Este perfil não tem e-mail cadastrado e por isso não consegue entrar. Peça ao administrador.',
  // Uma mensagem para chave inexistente e chave revogada: separá-las diria a quem tem uma
  // chave vazada se ela já foi descoberta.
  [ERROR_CODE.API_KEY_INVALID]: 'Chave de API inválida ou revogada.',
  [ERROR_CODE.API_KEY_READ_ONLY]:
    'A chave de API só lê. Esta operação altera o sistema e exige uma sessão de pessoa.',
  [ERROR_CODE.API_KEY_NOT_FOUND]: 'Chave de API não encontrada.',
  [ERROR_CODE.INVALID_INPUT]: 'Verifique os campos destacados.',
  [ERROR_CODE.PROJECT_NOT_FOUND]: 'Obra não encontrada.',
  [ERROR_CODE.PROJECT_CODE_TAKEN]: 'Já existe uma obra com este código.',
  [ERROR_CODE.PROJECT_IS_VERSION]:
    'Esta obra é uma versão de orçamento: a execução acompanha a versão aprovada.',
  [ERROR_CODE.PROJECT_NOT_A_VERSION]:
    'Esta obra já é a aprovada. Só uma versão de orçamento pode ser aprovada.',
  [ERROR_CODE.PROJECT_IS_APPROVED]:
    'Esta é a obra aprovada, a que a execução acompanha. Só uma versão de orçamento pode ser excluída aqui.',
  [ERROR_CODE.PROJECT_HAS_EXECUTION]:
    'Esta versão guarda execução (relatório diário, alocação, apontamento ou qualidade) que ficou nela na aprovação. Ela não se exclui: a execução se perderia.',
  [ERROR_CODE.PROJECT_VERSION_TRANSFER_CONFLICTS]:
    'Parte da operação da obra aprovada não tem lugar na versão, ou cai no mesmo lugar de outra. Escolha o destino de cada uma antes de aprovar.',
  [ERROR_CODE.PROJECT_SERVICE_TRANSFER_NOT_A_VERSION]:
    'Esta já é a obra aprovada. O quantitativo se leva de uma versão de orçamento para ela.',
  [ERROR_CODE.PROJECT_SERVICE_TRANSFER_UNKNOWN_SERVICE]:
    'Este serviço não tem quantitativo nesta versão, e não há o que levar.',
  [ERROR_CODE.PROJECT_SERVICE_TRANSFER_APPROVED]:
    'Este serviço já foi aprovado na obra aprovada, e serviço aprovado não reabre: a quantidade dele lá não muda mais.',
  [ERROR_CODE.PROJECT_SERVICE_TRANSFER_GAPS]:
    'A obra aprovada não tem onde receber parte deste quantitativo. Crie lá o que falta, ou leve a versão inteira pelo "Aprovar versão".',
  [ERROR_CODE.PROJECT_BUDGET_SERVICE_APPROVED]:
    'Este serviço já foi aprovado, e serviço aprovado não reabre.',
  [ERROR_CODE.PROJECT_BUDGET_LINE_WITHOUT_PRICE]:
    'O serviço só é aprovado com todas as linhas com preço. Informe o provisório do que falta.',
  [ERROR_CODE.PROJECT_BUDGET_IN_VERSION]:
    'Esta obra é uma versão de orçamento: os serviços são aprovados na obra aprovada.',
  [ERROR_CODE.PROJECT_BUDGET_SERVICES_MISSING]:
    'A obra aprovada aprovou serviços depois que esta versão foi criada, e aprovar a versão os destravaria. Duplique a obra aprovada de novo.',
  [ERROR_CODE.PROJECT_BUDGET_SERVICE_LOCKED]:
    'Este serviço foi aprovado no orçamento, e a quantidade dele não muda mais.',
  [ERROR_CODE.QUOTA_EXCEEDED]: 'O espaço de armazenamento do navegador acabou.',
  [ERROR_CODE.STORAGE_UNAVAILABLE]:
    'O navegador bloqueou o armazenamento local. Verifique se a janela anônima ou uma política do navegador está impedindo o uso.',
  [ERROR_CODE.FILE_NOT_FOUND]: 'Arquivo não encontrado no armazenamento.',
  [ERROR_CODE.REQUEST_TOO_LARGE]:
    'O arquivo é grande demais para o caminho até o servidor, e foi recusado antes de chegar lá.',
  [ERROR_CODE.NO_CONNECTION]:
    'Sem conexão com o servidor. Sem sinal, o sistema mostra o que já foi aberto, mas ainda não grava: tente de novo quando a conexão voltar.',
  [ERROR_CODE.UNREADABLE_RESPONSE]:
    'O servidor respondeu algo que o sistema não entendeu. Se estiver numa rede com portal de acesso, conclua a entrada nela e tente de novo.',
  [ERROR_CODE.DEVICE_OPERATION_MISMATCH]:
    'Este registro do aparelho já foi usado para outra gravação. Descarte-o e faça de novo.',
  [ERROR_CODE.DEVICE_OPERATION_BUSY]:
    'Este registro do aparelho ainda está sendo gravado. Ele sobe sozinho em seguida.',
  [ERROR_CODE.UNEXPECTED]:
    'Ocorreu um erro inesperado. Nenhuma alteração foi salva. Se persistir, recarregue a página.',
};

export function messageForCode(code: string, fallback?: string): string {
  return ERROR_MESSAGE[code] ?? fallback ?? ERROR_MESSAGE[ERROR_CODE.UNEXPECTED]!;
}
